import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Clock, Loader, Unlink } from 'lucide-react'
import { use, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { AuthContext } from '@/components/auth/context'
import { PasswordInput } from '@/components/sv/password-input'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { RESET_PASSWORD } from '@/config/recaptcha'
import { gql } from '@/lib/graphql/gql'
import { useRecaptcha, useSuspenseQuery } from '@/lib/hooks'
import { SevallaLogo } from '@/logos'
import type {
  ResetPasswordMutation,
  ResetPasswordMutationVariables,
  VerifyTokenQuery,
  VerifyTokenQueryVariables,
} from '@/routes/login/reset-password/index.types.gen'

const VERIFY_TOKEN_QUERY = gql`
  query VerifyToken($token: String!) {
    verifyToken(token: $token) {
      error
    }
  }
`

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword(
    $token: String!
    $password: String!
    $recaptchaToken: String!
  ) {
    resetPassword(
      token: $token
      password: $password
      recaptchaToken: $recaptchaToken
    ) {
      token
      error
    }
  }
`

const schema = z.object({
  password: z
    .string('New password is required')
    .regex(
      /^(?=.{8,})(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).*$/,
      'Password must contain a min. of 8 characters, at least one lowercase and capital letter, and a number.'
    )
    .min(8, 'New password must be at least 8 characters long')
    .max(150, 'New password must be at most 150 characters long'),
})

export const ResetPassword = () => {
  const { token = '' } = useParams()
  const [isPending, startTransition] = useTransition()
  const navigate = useNavigate()
  const { isAuthenticated = false } = use(AuthContext) ?? {}
  const getRecaptchaToken = useRecaptcha()

  const { data } = useSuspenseQuery<
    VerifyTokenQuery,
    VerifyTokenQueryVariables
  >(VERIFY_TOKEN_QUERY, {
    variables: { token },
  })
  const verificationError = data?.verifyToken?.error

  const [resetPassword] = useMutation<
    ResetPasswordMutation,
    ResetPasswordMutationVariables
  >(RESET_PASSWORD_MUTATION)

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: '',
    },
  })

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const isValid = await form.trigger()
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        const recaptchaToken = await getRecaptchaToken(RESET_PASSWORD)
        await resetPassword({
          variables: {
            token,
            password: data.password,
            recaptchaToken,
          },
        })
        toast(
          <ToasterContent variant="success">
            Password updated successfully
          </ToasterContent>,
          { position: 'top-right' }
        )

        navigate({ pathname: '/login' }, { replace: true })
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to update password
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <a
          href="https://sevalla.com"
          className="flex items-center gap-2 self-center"
        >
          <SevallaLogo className="w-24 h-auto" />
        </a>
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display text-xl">
                Set a new password
              </CardTitle>
              <CardDescription>
                Last step before you’re back in!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  {verificationError === 'EXPIRED' ? (
                    <Alert variant="destructive">
                      <Clock />
                      <AlertTitle>Expired link</AlertTitle>
                      <AlertDescription>
                        <div>
                          The password reset link has expired. Please{' '}
                          <Link
                            to="/login/request-reset-password"
                            className="underline underline-offset-4"
                          >
                            request a new one
                          </Link>
                          .
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  {verificationError === 'INVALID' ? (
                    <Alert variant="destructive">
                      <Unlink />
                      <AlertTitle>Invalid link</AlertTitle>
                      <AlertDescription>
                        <div>
                          The password reset link is invalid. Please{' '}
                          <Link
                            to="/login/request-reset-password"
                            className="underline underline-offset-4"
                          >
                            request a new one
                          </Link>
                          .
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New password</FormLabel>
                        <FormControl>
                          <PasswordInput
                            disabled={Boolean(verificationError)}
                            autoComplete="off"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isPending || Boolean(verificationError)}
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {isPending ? <Loader className="animate-spin" /> : null}
                    Save new password
                  </Button>
                </form>
              </Form>
              <div className="mt-4 text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank">
                  Terms of Service
                </a>{' '}
                apply.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
